import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Field, Form, FormikValues } from "formik";
import { withTranslation } from "react-i18next";
import CheckboxCards from "./CheckBoxCards/CheckBoxCards";
import { UserRoleEnum } from "../../../helpers/enums";
import "./RegistartionBackofficeOperatorForm.scss";
import { UserOpearatorsReq, BackOfficeSignUpReq } from "./models";
import { client } from "../../../App";
import { CREATE_BACKOFFICE_OPERATOR, getEvictionForMutation } from "../../../helpers/mutations";
import {
  getValidationRule,
  getComuniServiti,
  handlerErrorsOperatorRegister,
} from "../../../helpers/helpers";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../reducers/app";
import SuggestionInputField from "../../CaseGenerator/FormFields/SuggestionInputField";
import Modal from "../../common/Modal/Modal";

const roles = [
  UserRoleEnum.MANAGER,
  UserRoleEnum.OPERATOR,
  UserRoleEnum.MUNICIPAL_OPERATOR,
];

const RegistartionBackofficeOperatorForm = (props: any) => {
  const { t } = props;
  const [activeCheckbox, setActiveCheckbox] = useState(0);
  const [flowFinished, setFlowFinished] = useState(false);
  const [genericError, setGenericError] = useState(null);
  const dispatch = useDispatch();

  const [errors] = useState({
    code: 0,
    hasError: false,
  });

  const schemaValidation = () => {
    const validationEmail = getValidationRule("email");
    const validationUsername = /^[^\s- 0-9()@]+$/;

    return Yup.object().shape({
      name: Yup.string().required(t("common.nameRequired")),
      surname: Yup.string().required(t("common.cognomeRequired")),
      username: Yup.string()
        .matches(validationUsername, {
          message: t("error.username.space.match"),
        })
        .required(t("common.usernameRequired")),
      email: Yup.string()
        .matches(validationEmail, {
          message: t("errors.registration.company.email.match"),
        })
        .required(t("common.emailRequired")),
      operatorCity:
        activeCheckbox === 2
          ? Yup.string().required(t("common.cityRequired"))
          : Yup.string(),
    });
  };

  const initialValues = {
    name: "",
    surname: "",
    username: "",
    email: "",
    operatorCity: "",
  };

  const registerOperatorMutation = (data: BackOfficeSignUpReq) =>
    client(true).mutate({
      mutation: CREATE_BACKOFFICE_OPERATOR,
      variables: { data: data },
      update: (cache: any) => {
        const fieldsToEvict = getEvictionForMutation("backOfficeSignUp");
        fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
        cache.gc();
      },
    });

  const handleSubmit = async (values: FormikValues, props: any) => {
    const role = roles[activeCheckbox];
    const { setErrors } = props;
    dispatch(showLoading());
    const data = UserOpearatorsReq.makeData({ ...values, role });
    try {
      const response = await registerOperatorMutation(data);
      if (response) {
        dispatch(hideLoading());
        setFlowFinished(true);
      }
    } catch (e) {
      dispatch(hideLoading());
      handlerErrorsOperatorRegister(e, setErrors, setGenericError);
    }
  };

  const errorHandler = () => {
    if (!errors.hasError) return null;
    switch (errors.code) {
      case 500:
        return (
          <div className="row">
            <p className="submit-error">{t("common.serverError")}</p>
          </div>
        );
      case 400:
        return (
          <div className="row">
            <p className="submit-error">{t("common.formOnSubmitError")}</p>
          </div>
        );
    }
  };
  return (
    <div
      className={"RegistartionBackofficeOperatorForm mt-5 container"}
      data-testid="RegistartionBackofficeOperatorForm"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={schemaValidation}
        onSubmit={(value: FormikValues, props: any) =>
          handleSubmit(value, props)
        }
      >
        {({ errors, touched }) => (
          <Form noValidate autoComplete="off" role="presentation">
            <p className={"case-step-mandatory-fields-legend paragraph-small-fs9"}>{t("mandatory.fields.legend")}</p>
            <div className="row">
              <div className="col-xs-12 col-md-12 text-left">
                <div className="row container registrationBackofficeOperatorFormTitle">
                  <h2 className="headline-bold-fs2 ">{t("registerOperator.insertNewOperators")}</h2>
                </div>
                <div className={"row"}>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.name && touched.name
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label className="paragraph-small-fs9" htmlFor="name">
                        {t("registration.name.value")}*
                      </label>
                      <Field
                        name={"name"}
                        type="text"
                        id="name"
                        className={
                          errors.name && touched.name
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.name && touched.name ? "name-error" : null
                        }
                        required={true}
                        placeholder={t("registerOperator.name.placeholder")}
                      />
                      {errors.name && touched.name ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"name-error"}
                        >
                          {errors.name}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.name && touched.name
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label className="paragraph-small-fs9" htmlFor="surname">
                        {t("registerOperator.surname.username")}
                      </label>
                      <Field
                        name={"surname"}
                        type="text"
                        id="surname"
                        className={
                          errors.surname && touched.surname
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.surname && touched.surname
                            ? "surname-error"
                            : null
                        }
                        required={true}
                        placeholder={t("registerOperator.surname.placeholder")}
                      />
                      {errors.surname && touched.surname ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"surname-error"}
                        >
                          {errors.surname}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.name && touched.name
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label className="paragraph-small-fs9" htmlFor="username">
                        {t("registerOperator.username.username")}*
                      </label>
                      <Field
                        name={"username"}
                        type="text"
                        id="username"
                        className={
                          errors.username && touched.username
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.username && touched.username
                            ? "username-error"
                            : null
                        }
                        required={true}
                        placeholder={t("registerOperator.username.placeholder")}
                      />
                      {errors.username && touched.username ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"username-error"}
                        >
                          {errors.username}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12">
                    <div
                      className={
                        errors.name && touched.name
                          ? "form-group has-error"
                          : "form-group"
                      }
                    >
                      <label className="paragraph-small-fs9" htmlFor="email">
                        {t("registerOperator.email.email")}
                      </label>
                      <Field
                        name={"email"}
                        type="text"
                        id="email"
                        className={
                          errors.email && touched.email
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        aria-describedby={
                          errors.email && touched.email ? "email-error" : null
                        }
                        required={true}
                        placeholder={t("registerOperator.email.placeholder")}
                      />
                      {errors.email && touched.email ? (
                        <p
                          className="form-error-text paragraph-small-form"
                          role="alert"
                          id={"email-error"}
                        >
                          {errors.email}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {activeCheckbox === 2 ? (
                    <div className="col-lg-6 col-md-12">
                      <div
                        className={
                          errors.name && touched.name
                            ? "form-group has-error"
                            : "form-group"
                        }
                      >
                        <label
                          className="paragraph-small-fs9"
                          htmlFor="operatorCity"
                        >
                          {t("registerOperator.city.city")}
                        </label>

                        <SuggestionInputField
                          placeholder={t("registerOperator.city.placeholder")}
                          name={"operatorCity"}
                          type="text"
                          id="operatorCity"
                          getItems={async (setData: any) => {
                            try {
                              const items = await getComuniServiti();
                              return setData(items);
                            } catch (error) {
                              console.log(
                                "Error fetching comuni serviti: ",
                                error
                              );
                              setData([]);
                            }
                          }}
                          getSuggestions={(data: any) => {
                            return data;
                          }}
                          filter={(value: string, data: any) => {
                            return data.filter((item: any) => {
                              return (
                                String(item.comune)
                                  .toLowerCase()
                                  .indexOf(String(value).toLowerCase()) > -1
                              );
                            });
                          }}
                          onSelected={(value: string, data: any) => {
                            const [selected] = data.filter(
                              (item: any) =>
                                String(item.comune).toLowerCase() ===
                                String(value).toLowerCase()
                            );
                            if (selected) {
                            }
                          }}
                          onBlur={(value: string, data: any, setValue: any) => {
                            const [selected] = data.filter(
                              (item: any) =>
                                String(item.comune).toLowerCase() ===
                                String(value).toLowerCase()
                            );
                            selected ? setValue(selected.comune) : setValue("");
                          }}
                        />
                      </div>
                    </div>
                  ) : null}

                  <CheckboxCards
                    t={t}
                    activeCheckbox={(e: number) => setActiveCheckbox(e)}
                  />
                  <Modal
                    icon={"gc-icon-functional-check-2-col"}
                    to={"/"}
                    type={"link"}
                    linkTitle={t("common.goToDashboard")}
                    show={flowFinished}
                    onClose={() => setFlowFinished(false)}
                    onFirstClick={() => setFlowFinished(false)}
                    description={t("registerOperator.confirmCreate")}
                  />
                  {genericError && (
                    <div className={"row mt-3"}>
                      <div className={"col-sm-12"}>
                        <div
                          className={
                            "generic-error d-flex justify-content-center"
                          }
                        >
                          {t(genericError)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="submit-holder col-md-12">
                    <div className="form-group d-flex justify-content-end">
                      <button type="submit" className={"btn btn-primary"}>
                        {t("common.create")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {errors.hasError && errorHandler()}
    </div>
  );
};

export default withTranslation()(RegistartionBackofficeOperatorForm);
